import type { GateOverrideEntry } from '../types/scenario';

export const GATE_PENDING_TIMEOUT_MS = 3000;

export type GateUiState = 'OPEN' | 'CLOSED' | 'OPENING' | 'CLOSING' | 'ERROR';

export function stableGateUiState(state: 'open' | 'closed'): 'OPEN' | 'CLOSED' {
  return state === 'open' ? 'OPEN' : 'CLOSED';
}

export function transitionalGateUiState(
  state: 'open' | 'closed',
): 'OPENING' | 'CLOSING' {
  return state === 'open' ? 'OPENING' : 'CLOSING';
}

export function finalizeGateOverride(entry: GateOverrideEntry): GateOverrideEntry {
  return {
    ...entry,
    ui_state: stableGateUiState(entry.state),
    pending_command_id: undefined,
  };
}

export function finalizeGateOverrides(
  gateOverrides: Record<string, GateOverrideEntry>,
): Record<string, GateOverrideEntry> {
  const next: Record<string, GateOverrideEntry> = {};
  for (const [gateId, entry] of Object.entries(gateOverrides)) {
    next[gateId] = finalizeGateOverride(entry);
  }
  return next;
}

export function reconcileGateOverride(
  entry: GateOverrideEntry,
  serverState: 'open' | 'closed' | undefined,
): GateOverrideEntry {
  if (!entry.pending_command_id) {
    return entry;
  }
  if (serverState && serverState === entry.state) {
    return finalizeGateOverride({ ...entry, state: serverState });
  }
  return entry;
}

export function reconcileGateOverrides(
  gateOverrides: Record<string, GateOverrideEntry>,
  serverGateStates: Record<string, string>,
): Record<string, GateOverrideEntry> {
  let changed = false;
  const next: Record<string, GateOverrideEntry> = { ...gateOverrides };
  for (const [gateId, entry] of Object.entries(gateOverrides)) {
    if (!entry.pending_command_id) {
      continue;
    }
    const serverState = serverGateStates[gateId] as 'open' | 'closed' | undefined;
    const reconciled = reconcileGateOverride(entry, serverState);
    if (reconciled !== entry) {
      changed = true;
      next[gateId] = reconciled;
    }
  }
  return changed ? next : gateOverrides;
}

export function isGateToggleDeliverable(
  runStatus: string,
  runId: string | null,
  wsReadyState: number | undefined,
): boolean {
  return (
    runStatus === 'running' &&
    runId != null &&
    wsReadyState === WebSocket.OPEN
  );
}

export function resolveGateDisplayUiState(
  gateState: 'open' | 'closed',
  entry?: GateOverrideEntry,
): GateUiState | undefined {
  if (!entry) {
    return undefined;
  }
  if (entry.pending_command_id && gateState === entry.state) {
    return stableGateUiState(gateState);
  }
  return entry.ui_state as GateUiState | undefined;
}
